import { getNumber } from '@utils/Format'
import {
  OPEN_FILE_PREVIEW_EXTENSIONS,
  AAWZ_DRIVE_FILE_ICONS_MAP,
} from '@modules/aawz-drive/constants/Text'
import { aawzDate } from '@utils/dates/aawz-date'
import { aawzDrive } from '@modules/aawz-drive/constants/URL.js'

/**
 * Formata um tamanho em bytes para texto legivel.
 *
 * Quando usar: exibicao de tamanho na tabela/preview do drive.
 *
 * @param {number} size - Tamanho em bytes
 * @returns {string} Tamanho formatado (B, KB, MB, GB) ou '-' se invalido
 */
export const formatSize = (size) => {
  if (typeof size !== 'number') {
    return '-'
  }

  const kb = 1024
  const mb = kb * 1024
  const gb = mb * 1024

  if (size < kb) {
    return `${size} B`
  }
  if (size < mb) {
    return `${(size / kb).toFixed(1)} KB`
  }
  if (size < gb) {
    return `${(size / mb).toFixed(1)} MB`
  }
  return `${(size / gb).toFixed(1)} GB`
}

export const validateRequiredFields = ({ required = [], message }) => {
  const isValid = required.every(Boolean)
  if (isValid) {
    return null
  }

  return {
    status: 400,
    message,
  }
}

export const normalizeUniqueIds = (ids = []) => {
  if (!Array.isArray(ids)) {
    return []
  }

  const map = {}
  const uniqueIds = []

  ids.forEach((id) => {
    if (id === null || id === undefined) {
      return
    }

    const parsedId = String(id)
    if (!parsedId || map[parsedId]) {
      return
    }

    map[parsedId] = true
    uniqueIds.push(parsedId)
  })

  return uniqueIds
}

export const normalizeDeletableDriveItems = (items = []) => {
  const safeItems = Array.isArray(items) ? items : []
  const unlockedItems = safeItems.filter((item) => {
    return item && !item.locked
  })
  const uniqueIds = normalizeUniqueIds(
    unlockedItems.map((item) => item && item.id)
  )
  const uniqueIdsMap = {}

  uniqueIds.forEach((id) => {
    uniqueIdsMap[id] = true
  })

  return unlockedItems.filter((item) => {
    const itemId =
      item && item.id !== null && item.id !== undefined ? String(item.id) : ''
    if (!itemId || !uniqueIdsMap[itemId]) {
      return false
    }
    uniqueIdsMap[itemId] = false
    return true
  })
}

/**
 * Verifica se um item do drive está bloqueado para edicao/exclusao.
 *
 * Quando usar: antes de qualquer operacao destrutiva em um item.
 *
 * @param {Object} item - Item do drive
 * @returns {boolean}
 */
export const isDriveItemLocked = (item) => {
  return Boolean(item && item.locked)
}

/**
 * Verifica se o elemento alvo de um evento pertence a um controle de selecao
 * (checkbox, radio), impedindo que o drag ou selecao por area seja iniciado.
 *
 * Quando usar: handlers de mousedown/click que precisam ignorar controles internos.
 *
 * @param {EventTarget|null} target - event.target
 * @returns {boolean}
 */
export const isDriveSelectionControlTarget = (target) => {
  if (!target || !target.closest) {
    return false
  }
  return Boolean(
    target.closest('[data-drive-selection-control]') ||
      target.closest('.v-input--selection-controls')
  )
}

const normalizeDriveUpdateItemId = (item) => {
  if (!item || item.id === null || item.id === undefined) {
    return ''
  }
  return String(item.id)
}

const normalizeDriveUpdateName = (item) => {
  if (!item) {
    return ''
  }

  if (item.name !== null && item.name !== undefined) {
    const parsedName = String(item.name).trim()
    if (parsedName) {
      return String(item.name)
    }
  }

  if (item.filename === null || item.filename === undefined) {
    return ''
  }

  const parsedFileName = String(item.filename).trim()
  return parsedFileName ? String(item.filename) : ''
}

const normalizeDriveUpdateFolderPath = (item) => {
  if (!item) {
    return ''
  }

  const parsedFolderPath =
    item.folder_path !== null && item.folder_path !== undefined
      ? item.folder_path
      : item.path

  if (parsedFolderPath === null || parsedFolderPath === undefined) {
    return ''
  }

  return String(parsedFolderPath)
}

const buildNormalizedDriveUpdateItem = (item) => {
  if (!item || typeof item !== 'object') {
    return null
  }

  const id = normalizeDriveUpdateItemId(item)
  if (!id) {
    return null
  }

  const name = normalizeDriveUpdateName(item)
  const folderPath = normalizeDriveUpdateFolderPath(item)
  if (!name && !folderPath) {
    return null
  }

  return {
    ...item,
    id,
    ...(name ? { name } : {}),
    ...(folderPath ? { folder_path: folderPath } : {}),
  }
}

export const normalizeDriveUpdatePayloadItems = (items = []) => {
  if (!Array.isArray(items)) {
    return []
  }

  const idsMap = {}
  const normalizedItems = []

  items.forEach((item) => {
    const normalizedItem = buildNormalizedDriveUpdateItem(item)
    const itemId = normalizedItem && normalizedItem.id
    if (!itemId || idsMap[itemId]) {
      return
    }

    idsMap[itemId] = true
    normalizedItems.push(normalizedItem)
  })

  return normalizedItems
}

export const getDriveFolderId = (folder) => {
  if (!folder || folder.id === null || folder.id === undefined) {
    return ''
  }
  return String(folder.id)
}

export const isInvalidDriveMoveTarget = (item, targetFolderId = '') => {
  if (!item || item.id === null || item.id === undefined) {
    return true
  }

  const itemId = String(item.id)
  if (!itemId || itemId === targetFolderId) {
    return true
  }

  if (item.type !== 'folder') {
    return false
  }

  if (!targetFolderId || targetFolderId === 'root') {
    return false
  }

  return targetFolderId.indexOf(`${itemId}/`) === 0
}

export const getValidDriveMoveItems = (items = [], targetFolderId = '') => {
  const safeItems = Array.isArray(items) ? items : []
  return safeItems.filter((item) => {
    return !isInvalidDriveMoveTarget(item, targetFolderId)
  })
}

export const buildDriveMoveDropPayload = ({
  folder,
  currentFolder,
  dragItems = [],
} = {}) => {
  const targetFolderId = getDriveFolderId(folder)
  if (!targetFolderId) {
    return {
      targetFolderId: '',
      itemsToMove: [],
      shouldWarn: false,
    }
  }

  const currentFolderId = getDriveFolderId(currentFolder)
  if (targetFolderId === currentFolderId) {
    return {
      targetFolderId,
      itemsToMove: [],
      shouldWarn: false,
    }
  }

  const itemsToMove = getValidDriveMoveItems(dragItems, targetFolderId)
  return {
    targetFolderId,
    itemsToMove,
    shouldWarn: !itemsToMove.length,
  }
}

export const normalizeDriveDragItemId = (value) => {
  if (value === null || value === undefined) {
    return ''
  }
  return String(value)
}

export const isDriveDragLeaveToDescendant = (event) => {
  const currentTarget = event && event.currentTarget
  const relatedTarget = event && event.relatedTarget

  return Boolean(
    currentTarget &&
      relatedTarget &&
      currentTarget.contains &&
      currentTarget.contains(relatedTarget)
  )
}

export const prepareDriveDragOverEvent = (event) => {
  if (event && event.preventDefault) {
    event.preventDefault()
  }

  if (event && event.dataTransfer) {
    event.dataTransfer.dropEffect = 'move'
  }
}

export const consumeDriveDropEvent = (event) => {
  if (event && event.preventDefault) {
    event.preventDefault()
  }
  if (event && event.stopPropagation) {
    event.stopPropagation()
  }
}

const getDriveDragPreviewPrimaryItem = (dragItems = []) => {
  if (!Array.isArray(dragItems) || !dragItems.length) {
    return null
  }
  return dragItems[0]
}

const getDriveDragPreviewItemName = (item) => {
  if (!item) {
    return 'Item'
  }
  return item.displayName || item.name || item.filename || 'Item'
}

const getDriveDragPreviewItemIconClass = (item) => {
  if (item && item.type === 'folder') {
    return 'mdi-folder'
  }
  return 'mdi-file-outline'
}

const createDriveDragPreviewIconElement = (item, iconHtml = '') => {
  const iconWrap = document.createElement('div')
  iconWrap.className = 'aawz-drive-drag-preview__icon'

  if (iconHtml) {
    iconWrap.innerHTML = iconHtml
    return iconWrap
  }

  const iconEl = document.createElement('i')
  iconEl.className = `v-icon notranslate mdi ${getDriveDragPreviewItemIconClass(
    item
  )}`
  iconWrap.appendChild(iconEl)

  if (item && item.iconConfig && item.iconConfig.color) {
    iconWrap.style.color = item.iconConfig.color
  }

  return iconWrap
}

const createDriveDragPreviewNameElement = (item) => {
  const nameEl = document.createElement('span')
  nameEl.className = 'aawz-drive-drag-preview__name'
  nameEl.innerText = getDriveDragPreviewItemName(item)
  return nameEl
}

const createDriveDragPreviewCountElement = (totalItems) => {
  const countEl = document.createElement('span')
  countEl.className = 'aawz-drive-drag-preview__count'
  countEl.innerText = String(totalItems)
  return countEl
}

const createDriveDragPreviewCardElement = ({ totalItems, item, iconHtml }) => {
  const cardEl = document.createElement('div')
  cardEl.className = 'aawz-drive-drag-preview__card'
  cardEl.appendChild(createDriveDragPreviewIconElement(item, iconHtml || ''))
  cardEl.appendChild(createDriveDragPreviewNameElement(item))
  cardEl.appendChild(createDriveDragPreviewCountElement(totalItems))
  return cardEl
}

export const getDriveDragPreviewPayload = (dragItems = []) => {
  const totalItems = Array.isArray(dragItems) ? dragItems.length : 0
  const item = getDriveDragPreviewPrimaryItem(dragItems)
  return {
    totalItems,
    item,
  }
}

export const createDriveDragPreviewElement = ({
  totalItems = 0,
  item = null,
  iconHtml = '',
} = {}) => {
  if (typeof document === 'undefined' || !totalItems || !item) {
    return null
  }

  const previewEl = document.createElement('div')
  previewEl.className = 'aawz-drive-drag-preview'
  if (totalItems > 1) {
    previewEl.classList.add('has-stack')
  }

  previewEl.appendChild(
    createDriveDragPreviewCardElement({
      totalItems,
      item,
      iconHtml,
    })
  )

  document.body.appendChild(previewEl)
  return previewEl
}

export const destroyDriveDragPreviewElement = (previewEl) => {
  if (!previewEl) {
    return
  }

  if (previewEl.parentNode) {
    previewEl.parentNode.removeChild(previewEl)
  }
}

export const buildDeleteResponseValueFromBatchItem = (batchItemValue = {}) => {
  if (!batchItemValue || typeof batchItemValue !== 'object') {
    return null
  }

  let ids = []
  if (Array.isArray(batchItemValue.items) && batchItemValue.items.length) {
    ids = batchItemValue.items
      .map((item) => {
        if (item && typeof item === 'object') {
          return item.id
        }
        return item
      })
      .filter(Boolean)
  } else if (batchItemValue.id) {
    ids = [batchItemValue.id]
  }

  if (!ids.length) {
    return null
  }

  return {
    items: ids.map((id) => ({ id })),
    parent: batchItemValue.parent || null,
    storage: batchItemValue.storage || null,
  }
}

export const buildUrl = ({ scope, rootKey, drive_root_name }) => {
  const url =
    scope === 'office'
      ? aawzDrive.officeGetFiles({
          drive_root_name: drive_root_name,
        })
      : aawzDrive.professionalGetFiles({
          id_cliente_app: rootKey,
          drive_root_name: drive_root_name,
        })

  return url
}

export const getDriveScope = (scope) => {
  return scope === 'office' ? 'office' : 'client'
}

export const getDriveActionUrl = ({
  scope,
  rootKey,
  itemId,
  action,
  folder,
} = {}) => {
  const currentScope = getDriveScope(scope)
  const isOffice = currentScope === 'office'
  const actionMap = {
    upload: () =>
      isOffice
        ? aawzDrive.officeUploadFile()
        : aawzDrive.professionalUploadFile({ id_cliente_app: rootKey }),
    createFolder: () =>
      isOffice
        ? aawzDrive.officeCreateFolder()
        : aawzDrive.professionalCreateFolder({ id_cliente_app: rootKey }),
    download: () =>
      isOffice
        ? aawzDrive.officeDownloadFile({ drive_file_id: itemId })
        : aawzDrive.professionalDownloadFile({
            id_cliente_app: rootKey,
            drive_file_id: itemId,
          }),
    downloadItems: () =>
      isOffice
        ? aawzDrive.officeDownloadItems()
        : aawzDrive.professionalDownloadItems({
            id_cliente_app: rootKey,
          }),
    deleteItems: () =>
      isOffice
        ? aawzDrive.officeDeleteItems()
        : aawzDrive.professionalDeleteItems({
            id_cliente_app: rootKey,
          }),
    updateItems: () =>
      isOffice
        ? aawzDrive.officeUpdateItems()
        : aawzDrive.professionalUpdateItems({
            id_cliente_app: rootKey,
          }),
    delete: () =>
      isOffice
        ? aawzDrive.officeDeleteFile({ drive_item_id: itemId })
        : aawzDrive.professionalDeleteFile({
            id_cliente_app: rootKey,
            drive_item_id: itemId,
          }),
    rename: () =>
      isOffice
        ? aawzDrive.officeRenameFile({ drive_item_id: itemId })
        : aawzDrive.professionalRenameFile({
            id_cliente_app: rootKey,
            drive_item_id: itemId,
          }),
  }

  const resolver = actionMap[action]
  return resolver ? resolver() : ''
}

/**
 * Normaliza um item do drive para exibicao na tabela.
 *
 * Quando usar: antes de renderizar cada item no componente do drive.
 *
 * @param {Object} item - Item do drive
 * @returns {Object} Item normalizado com labels de exibição
 */
export const normalizeDriveItem = (item) => {
  const isFolder = item && item.type === 'folder'
  const itemId = item && item.id ? String(item.id) : ''
  const rawName = item ? item.name || item.filename || '' : ''
  const name = isFolder && itemId === 'clientes' ? 'Clientes' : rawName
  const extension = item && item.extension ? String(item.extension) : ''
  const createdAt = item && item.createdAt ? item.createdAt : '-'
  const extensionLabel = getExtensionLabel({ item, isFolder })
  const displayName = getDisplayName({ name, extension, isFolder })
  const canVisualize = getCanVisualize({ isFolder, extension })

  return {
    ...item,
    name,
    uploadDate: createdAt,
    sizeLabel: formatSize(item && item.size),
    extensionLabel: extensionLabel,
    displayName,
    canVisualize,
    iconConfig: getIconConfig(item),
  }
}

const isFolderItem = (item) => {
  return item && (item.type === 'folder' || item.is_folder)
}

const buildDriveSearchText = ({ displayName, name, extension, pathLabel }) => {
  return [displayName, name, extension, pathLabel]
    .filter(Boolean)
    .join(' ')
    .toLowerCase()
}

export const normalizeDriveSearchItem = ({
  item,
  pathNodes = [],
  includeSelfInPath = false,
} = {}) => {
  if (!item) {
    return null
  }
  const normalized = normalizeDriveItem(item)
  const nodes = includeSelfInPath ? pathNodes.concat(item) : pathNodes
  const path = nodes.map((node) => node && node.name).filter(Boolean)
  const pathIds = nodes.map((node) => node && node.id).filter(Boolean)
  const pathLabel = path.length ? path.join(' / ') : ''
  const name = normalized.name || normalized.filename || normalized.displayName
  const isFolder = isFolderItem(normalized)
  const type = isFolder ? 'folder' : 'file'
  const parent = getDriveParentSearchItem({ isFolder, path, pathIds })

  return {
    id: normalized.id,
    displayName: normalized.displayName || name || '',
    type,
    isFolder,
    extension: normalized.extensionLabel,
    iconConfig: normalized.iconConfig,
    pathIds,
    parent,
    searchText: buildDriveSearchText({
      displayName: normalized.displayName,
      name,
      extension: normalized.extensionLabel,
      pathLabel,
    }),
  }
}

export const buildDriveSearchItems = ({
  root,
  includeFolders = true,
  includeFiles = true,
} = {}) => {
  if (!root) {
    return []
  }

  const items = []

  const walk = (node, pathNodes = []) => {
    if (!node) return

    const isFolder = isFolderItem(node)
    const shouldInclude =
      (isFolder && includeFolders) || (!isFolder && includeFiles)

    if (node !== root && shouldInclude) {
      const normalized = normalizeDriveSearchItem({
        item: node,
        pathNodes,
        includeSelfInPath: isFolder,
      })
      if (normalized) {
        items.push(normalized)
      }
    }

    const children = Array.isArray(node.children) ? node.children : []
    const nextPathNodes = node.name ? pathNodes.concat(node) : pathNodes
    children.forEach((child) => walk(child, nextPathNodes))
  }

  walk(root, [])

  return items
}

export const buildDrivePathFromIds = ({ root, pathIds = [] } = {}) => {
  if (!root || !Array.isArray(pathIds) || !pathIds.length) {
    return root ? [root] : []
  }

  return rebuildValidPath(root, pathIds)
}

export const buildDriveShiftSelectionMap = ({
  orderedIds = [],
  anchorId,
  targetId,
  selectedIdsMap = {},
} = {}) => {
  if (!Array.isArray(orderedIds) || !orderedIds.length) {
    return { changed: false, selectedIdsMap }
  }

  const normalizedIds = orderedIds.map((id) => String(id))
  const fromIndex = normalizedIds.indexOf(String(anchorId))
  const toIndex = normalizedIds.indexOf(String(targetId))

  if (fromIndex < 0 || toIndex < 0) {
    return { changed: false, selectedIdsMap }
  }

  const start = Math.min(fromIndex, toIndex)
  const end = Math.max(fromIndex, toIndex)
  const nextMap = { ...(selectedIdsMap || {}) }

  for (let i = start; i <= end; i += 1) {
    nextMap[normalizedIds[i]] = true
  }

  return { changed: true, selectedIdsMap: nextMap }
}

export const normalizeDriveSelectionIds = (selectedIds = []) => {
  if (!Array.isArray(selectedIds)) {
    return []
  }

  return selectedIds
    .filter((id) => id !== null && id !== undefined)
    .map((id) => String(id))
}

export const getDriveSelectableItemIds = (items = []) => {
  if (!Array.isArray(items)) {
    return []
  }

  return items
    .filter((item) => item && item.id !== null && item.id !== undefined)
    .map((item) => String(item.id))
}

export const isDriveAllSelected = ({ selectedIds = [], itemIds = [] } = {}) => {
  if (!itemIds.length) {
    return false
  }

  return itemIds.every((id) => selectedIds.includes(id))
}

export const isDrivePartiallySelected = ({
  selectedIds = [],
  itemIds = [],
} = {}) => {
  if (!itemIds.length || !selectedIds.length) {
    return false
  }

  return !isDriveAllSelected({ selectedIds, itemIds })
}

export const buildDriveNextSelectionIds = ({
  selectedIds = [],
  itemId,
  value,
} = {}) => {
  if (itemId === null || itemId === undefined) {
    return selectedIds
  }

  const normalizedItemId = String(itemId)
  const currentIds = normalizeDriveSelectionIds(selectedIds)
  const hasItem = currentIds.includes(normalizedItemId)
  const shouldSelect = value !== undefined ? value : !hasItem

  if (shouldSelect && !hasItem) {
    return currentIds.concat(normalizedItemId)
  }

  if (!shouldSelect && hasItem) {
    return currentIds.filter((id) => id !== normalizedItemId)
  }

  return currentIds
}

export const buildDriveSelectAllSelectionIds = ({
  itemIds = [],
  isAllSelected = false,
  value,
} = {}) => {
  const shouldSelectAll = value !== undefined ? value : !isAllSelected
  return shouldSelectAll ? itemIds.slice() : []
}

export const buildDriveToggleSelectionState = ({
  selectedIds = [],
  itemId,
  multiSelectionEnabled = false,
} = {}) => {
  if (itemId === null || itemId === undefined) {
    return {
      selectedIds: normalizeDriveSelectionIds(selectedIds),
      selectedItemId: null,
      multiSelectionEnabled,
    }
  }

  const itemIdString = String(itemId)
  const normalizedSelectedIds = normalizeDriveSelectionIds(selectedIds)
  const isAlreadySelected = normalizedSelectedIds.includes(itemIdString)

  if (!isAlreadySelected) {
    const nextSelectedIds = normalizedSelectedIds.concat(itemIdString)
    return {
      selectedIds: nextSelectedIds,
      selectedItemId: itemIdString,
      multiSelectionEnabled:
        multiSelectionEnabled || nextSelectedIds.length > 1,
    }
  }

  const nextSelectedIds = normalizedSelectedIds.filter(
    (id) => id !== itemIdString
  )
  return {
    selectedIds: nextSelectedIds,
    selectedItemId: nextSelectedIds.length ? nextSelectedIds[0] : null,
    multiSelectionEnabled: nextSelectedIds.length
      ? multiSelectionEnabled
      : false,
  }
}

const getDriveParentSearchItem = ({ isFolder, path, pathIds }) => {
  const nameFactor = isFolder ? -2 : -1
  const pathFactor = isFolder ? -1 : 0
  const parentName = path.length ? path[path.length + nameFactor] : ''
  const parentPathIds = pathIds.slice(0, pathIds.length + pathFactor)

  return {
    name: parentName,
    pathIds: parentPathIds,
    type: 'folder',
  }
}

const parseDateValue = (value) => {
  if (!value || typeof value !== 'string') {
    return 0
  }

  const format = value.includes('/') ? 'DD/MM/YYYY' : null
  return aawzDate(value, format).valueOf() || 0
}

const getExtensionLabel = ({ item, isFolder }) => {
  if (isFolder) {
    return '-'
  }
  return item && item.extension ? item.extension : '-'
}

const getDisplayName = ({ name, extension, isFolder }) => {
  if (isFolder || !extension) {
    return name
  }
  return buildDisplayName(name, extension)
}

const buildDisplayName = (name, extension) => {
  if (!name) {
    return name
  }
  if (!extension) {
    return name
  }

  const safeExtension = String(extension).toLowerCase()
  const lowerName = String(name).toLowerCase()
  const suffix = `.${safeExtension}`
  if (lowerName.endsWith(suffix)) {
    return name
  }
  return `${name}${suffix}`
}

const getCanVisualize = ({ isFolder, extension }) => {
  if (isFolder || !extension) {
    return false
  }
  return OPEN_FILE_PREVIEW_EXTENSIONS.includes(String(extension).toLowerCase())
}

/**
 * Ordena itens do drive colocando pastas primeiro, depois por data de upload
 * (desc) e por tamanho (desc).
 *
 * Quando usar: antes de renderizar a lista de arquivos/pastas.
 *
 * @param {Array<Object>} items - Lista de itens do drive
 * @returns {Array<Object>} Lista ordenada
 */
export const sortAawzDriveItems = (items = []) => {
  if (!Array.isArray(items)) {
    return []
  }

  const decorated = items.map((item) => ({
    item,
    isFolder: isFolderItem(item) ? 0 : 1,
    date: parseDateValue(item && item.uploadDate),
    size: getNumber(item && item.size) || 0,
  }))

  decorated.sort((first, second) => {
    if (first.isFolder !== second.isFolder) {
      return first.isFolder - second.isFolder
    }
    if (first.date !== second.date) {
      return second.date - first.date
    }
    return second.size - first.size
  })

  return decorated.map((decoratedItem) => decoratedItem.item)
}

/**
 * Extrai uma lista de IDs validos de um caminho de navegacao (breadcrumbs).
 *
 * Quando usar: ao reconstruir o caminho atual apos recarregar a arvore.
 *
 * @param {Array<Object>} path
 * @returns {Array<string|number>} Lista de IDs
 */
const buildPathIds = (path) => {
  const pathIds = []
  const safePath = Array.isArray(path) ? path : []

  for (let i = 0; i < safePath.length; i += 1) {
    const item = safePath[i]
    if (item && item.id) {
      pathIds.push(item.id)
    }
  }

  return pathIds
}

/**
 * Encontra um filho direto pelo ID.
 *
 * Quando usar: durante a reconstrucao do caminho valido.
 *
 * @param {Object} parent
 * @param {string|number} childId
 * @returns {Object|null}
 */
const findChildById = (parent, childId) => {
  if (!parent || !Array.isArray(parent.children)) {
    return null
  }
  return parent.children.find((child) => child.id === childId) || null
}

/**
 * Reconstroi um caminho valido a partir da raiz usando uma lista de IDs.
 * Para no ultimo no valido se algum trecho nao existir.
 *
 * Quando usar: ao normalizar breadcrumbs depois de update na arvore.
 *
 * @param {Object} root
 * @param {Array<string|number>} pathIds
 * @returns {Array<Object>} Caminho valido reconstruido
 */
const rebuildValidPath = (root, pathIds) => {
  if (!pathIds.length || pathIds[0] !== root.id) {
    return [root]
  }

  const newPath = [root]
  let currentNode = root

  for (let i = 1; i < pathIds.length; i += 1) {
    const nextNode = findChildById(currentNode, pathIds[i])
    if (!nextNode) {
      break
    }
    newPath.push(nextNode)
    currentNode = nextNode
  }

  return newPath
}

/**
 * Normaliza o caminho atual com base na arvore mais recente.
 *
 * Garante que o caminho:
 * - Sempre comeca na raiz
 * - So contenha nos existentes
 * - Seja cortado no ultimo no valido quando algo foi removido
 *
 * Quando usar:
 * - Recarregar o drive
 * - Excluir pasta/arquivo
 * - Atualizar a arvore pelo backend
 * - Trocar o rootKey
 *
 * @param {Object} params
 * @param {Object} params.root - No raiz atual da arvore
 * @param {Array<Object>} params.currentPath - Caminho atual selecionado
 *
 * @returns {Array<Object>} Caminho valido a partir da raiz
 */
export const normalizeDrivePath = ({ root, currentPath }) => {
  if (!root) {
    return []
  }

  if (!Array.isArray(currentPath) || !currentPath.length) {
    return [root]
  }

  const pathIds = buildPathIds(currentPath)
  return rebuildValidPath(root, pathIds)
}

/**
 * Normaliza payload para um formato padrao usado pelas mutations do Vuex.
 *
 * Formato aceito:
 * - { rootKey, data }
 *
 * Quando usar: sempre que um payload chegar na mutation.
 *
 * @param {Object} payload
 * @returns {{ key: string, data: any } | null}
 */
export const normalizeAawzDrivePayload = (payload) => {
  if (!payload || typeof payload !== 'object') {
    return null
  }

  if (!('rootKey' in payload)) {
    return null
  }

  return {
    key:
      payload.rootKey !== null && payload.rootKey !== undefined
        ? String(payload.rootKey)
        : 'default',
    data: payload.data || null,
  }
}

/**
 * Cria um novo estado do drive substituindo os dados
 * associados ao rootKey.
 *
 * Quando usar: mutation SET, quando o GET traz a arvore completa.
 *
 * @param {Object} currentData
 * @param {Object} payload
 * @returns {Object|null}
 */
export const buildAawzDriveState = (currentData, payload) => {
  const normalized = normalizeAawzDrivePayload(payload)
  if (!normalized) {
    return null
  }

  return {
    ...(currentData || {}),
    [normalized.key]: normalized.data,
  }
}

/**
 * Mescla dados recebidos no estado atual do drive.
 *
 * Suporta:
 * - Merge de objeto simples
 * - Merge de lista (por id)
 * - Atualizacao de arvore via parent/item
 *
 * Quando usar: mutation ADD, apos POST/PUT (upload, criar pasta).
 *
 * @param {Object} currentData
 * @param {Object} payload
 * @returns {Object|null}
 */
export const mergeAawzDriveState = (currentData, payload) => {
  const normalized = normalizeAawzDrivePayload(payload)
  if (!normalized || !normalized.data) {
    return null
  }

  const dataToMerge = JSON.parse(JSON.stringify(normalized.data))
  if (!isParentItemPayload(dataToMerge)) {
    return null
  }

  return mergeParentItemPayload(
    currentData ? { ...currentData } : {},
    normalized.key,
    dataToMerge
  )
}

/**
 * Exclui itens (e seus descendentes) da arvore usando uma lista de IDs.
 *
 * Quando usar: mutation DELETE, depois do DELETE no backend.
 *
 * @param {Object} currentData
 * @param {Object} payload
 * @returns {Object|null}
 */
export const deleteAawzDriveState = (currentData, payload) => {
  const parsed = getDeletePayload(payload)
  if (!parsed) {
    return null
  }

  const next = currentData ? JSON.parse(JSON.stringify(currentData)) : {}
  const root = next[parsed.key]
  if (!root || typeof root !== 'object') {
    return null
  }

  deleteIdsFromTree(root, parsed.ids)
  updateAncestorSizes(root, parsed.parent)
  return next
}

const extractDeleteIds = (items) => {
  if (!Array.isArray(items)) {
    return []
  }

  return items
    .map((item) => {
      if (item && typeof item === 'object') {
        return item.id
      }
      return item
    })
    .filter(Boolean)
}

const getDeletePayload = (payload) => {
  const normalized = normalizeAawzDrivePayload(payload)
  if (!normalized || !normalized.data) {
    return null
  }

  const ids = extractDeleteIds(normalized.data.items)
  if (!ids.length) {
    return null
  }

  return {
    key: normalized.key,
    ids,
    parent: normalized.data.parent,
  }
}

const deleteIdsFromTree = (root, ids) => {
  const idsSet = new Set(ids)
  const deleteFromNode = (node) => {
    if (!node || !Array.isArray(node.children)) {
      return
    }

    node.children = node.children.filter((child) => {
      if (!child || idsSet.has(child.id)) {
        return false
      }
      deleteFromNode(child)
      return true
    })
  }

  deleteFromNode(root)
}

/**
 * Atualiza um item na arvore pelo id (menos children)
 *
 * Quando usar: renomear arquivo/pasta (payload com item).
 *
 * @param {Object} currentData
 * @param {Object} payload
 * @returns {Object|null}
 */
export const updateAawzDriveItemById = (currentData, payload) => {
  const normalized = normalizeAawzDrivePayload(payload)
  const item = normalized && normalized.data
  if (!normalized || !item || !item.id) {
    return null
  }

  const next = currentData ? JSON.parse(JSON.stringify(currentData)) : {}
  const root = next[normalized.key]
  if (!root || typeof root !== 'object') {
    return null
  }

  const normalizedItem = normalizeTreeItem(item)
  const updated = updateNodeById(root, normalizedItem)
  return updated ? next : null
}

const updateNodeById = (node, normalizedItem) => {
  if (!node) {
    return false
  }

  if (node.id === normalizedItem.id) {
    applyItemUpdate(node, normalizedItem)
    return true
  }

  if (Array.isArray(node.children)) {
    return node.children.some((child) => updateNodeById(child, normalizedItem))
  }

  return false
}

const applyItemUpdate = (target, source) => {
  Object.keys(source).forEach((itemKey) => {
    if (itemKey === 'children') {
      return
    }
    target[itemKey] = source[itemKey]
  })
}

/**
 * Verifica se o payload representa atualizacao por parent/item.
 */
const isParentItemPayload = (data) => data && data.parent && data.item

/**
 * Extrai a data de criacao de um item.
 *
 * Quando usar: antes de inserir/atualizar um item em parent.children.
 */
const extractCreatedAt = (item) => {
  const raw =
    (item && item.createdAt) ||
    (item && item.data_registro) ||
    (item && item.data_atualizacao)
  if (!raw || typeof raw !== 'string') {
    return null
  }

  return raw.split(' ')[0] || null
}

/**
 * Normaliza um item vindo do backend para o formato esperado na arvore.
 *
 * Quando usar: antes de inserir/atualizar um item em parent.children.
 */
const normalizeTreeItem = (item) => {
  if (!item || typeof item !== 'object') {
    return item
  }

  const createdAt = extractCreatedAt(item)

  if (item.type) {
    return createdAt ? { ...item, createdAt } : item
  }

  if (item.is_folder) {
    return {
      ...item,
      ...(createdAt ? { createdAt } : {}),
      type: 'folder',
      children: Array.isArray(item.children) ? item.children : [],
    }
  }

  return {
    ...item,
    ...(createdAt ? { createdAt } : {}),
    type: 'file',
  }
}

/**
 * Mescla um item em um parent dentro da arvore.
 *
 * Usado quando o backend retorna payload com parent + item:
 * - criar pasta
 * - upload de arquivo
 * - renomear
 * - atualizar metadados
 *
 * Regras:
 * - Se o item ja existe em parent.children, ele e atualizado.
 * - Se nao existe, ele e adicionado.
 *
 * Exemplo de uso (mutation ADD):
 * mergeParentItemPayload(state, rootKey, { parent: { id }, item: { id, name } })
 *
 * @param {Object} state - Estado atual do drive
 * @param {string} key - rootKey do drive
 * @param {Object} data
 * @param {Object} data.parent - Referencia do parent (id)
 * @param {Object} data.item - Item que sera mesclado
 *
 * @returns {Object|null} Estado atualizado ou null se raiz for invalida
 */
const mergeParentItemPayload = (state, key, data) => {
  const root = state[key]
  if (!root || typeof root !== 'object') {
    return null
  }

  const normalizedItem = normalizeTreeItem(data.item)
  const parentNode = findParentNode(root, data.parent)
  if (!parentNode) {
    return null
  }

  if (!Array.isArray(parentNode.children)) {
    parentNode.children = []
  }

  updateAncestorSizes(root, data.parent)
  upsertChild(parentNode, normalizedItem)
  return state
}

const updateAncestorSizes = (root, parent) => {
  if (!root || !parent || typeof parent.size !== 'number') {
    return
  }

  const path = findNodePath(root, parent.id)
  if (!path || !path.length) {
    return
  }

  const currentSize =
    typeof path[path.length - 1].size === 'number'
      ? path[path.length - 1].size
      : 0
  const delta = parent.size - currentSize
  if (!delta) {
    return
  }

  path.forEach((node) => {
    const base = typeof node.size === 'number' ? node.size : 0
    node.size = base + delta
  })
}

const findNodePath = (node, targetId, path = []) => {
  if (!node) {
    return null
  }

  const nextPath = path.concat(node)
  if (node.id === targetId) {
    return nextPath
  }

  if (!Array.isArray(node.children)) {
    return null
  }

  for (let i = 0; i < node.children.length; i += 1) {
    const result = findNodePath(node.children[i], targetId, nextPath)
    if (result) {
      return result
    }
  }

  return null
}

const findParentNode = (root, parent) => {
  if (!root || !parent) {
    return null
  }

  const parentId = parent.id
  if (!parentId) {
    return null
  }

  return findNodeBy(root, (node) => {
    return node && node.type === 'folder' && node.id === parentId
  })
}

const findNodeBy = (node, predicate) => {
  if (!node) {
    return null
  }

  if (predicate(node)) {
    return node
  }

  if (!Array.isArray(node.children)) {
    return null
  }

  for (let i = 0; i < node.children.length; i += 1) {
    const found = findNodeBy(node.children[i], predicate)
    if (found) {
      return found
    }
  }

  return null
}

const upsertChild = (parentNode, item) => {
  const existing = parentNode.children.find((child) => child.id === item.id)

  if (existing) {
    Object.keys(item).forEach((itemKey) => {
      existing[itemKey] = item[itemKey]
    })
    return
  }

  parentNode.children.push(item)
}

/**
 * Monta configuracao do icone com base em extension/type.
 *
 * Quando usar: antes de renderizar cada linha da tabela.
 *
 * @param {Object} item - Item do drive
 * @returns {{ name: string, color: string, size: string }} Config do icone
 */
export const getIconConfig = (item) => {
  const extension =
    item && item.extension ? String(item.extension).toLowerCase() : ''
  const type = item && item.type ? String(item.type).toLowerCase() : ''
  const map = AAWZ_DRIVE_FILE_ICONS_MAP

  return map[extension] || map[type] || map.file
}
/**
 * Encontra o ancestral scrollavel mais proximo de um elemento DOM.
 * Retorna `window` se nenhum ancestral scrollavel for encontrado.
 *
 * @param {HTMLElement} el - Elemento DOM
 * @returns {HTMLElement|Window} Ancestral scrollavel ou window
 */
export const findNearestScrollableAncestor = (el) => {
  let current = el ? el.parentElement : null
  while (current && current !== document.body) {
    const style = window.getComputedStyle(current)
    const overflowY = style && style.overflowY ? style.overflowY : ''
    const overflow = style && style.overflow ? style.overflow : ''
    const isScrollable = /auto|scroll/.test(overflowY + overflow)
    if (isScrollable && current.scrollHeight > current.clientHeight) {
      return current
    }
    current = current.parentElement
  }
  return window
}

/**
 * Percorre os ancestrais entre `el` e `scrollContainer` e forca
 * overflow:visible em qualquer um que tenha overflow:hidden, que quebraria
 * position:sticky dos filhos. Retorna um array de patches para restaurar depois.
 *
 * @param {HTMLElement} el - Elemento DOM
 * @param {HTMLElement} scrollContainer - Container scrollavel
 * @returns {Array} Array de patches para restaurar overflow
 */
export const patchHiddenOverflowAncestors = (el, scrollContainer) => {
  const patches = []
  let current = el ? el.parentElement : null
  while (current && current !== scrollContainer) {
    const style = window.getComputedStyle(current)
    const overflow = style && style.overflow ? style.overflow : ''
    const overflowY = style && style.overflowY ? style.overflowY : ''
    if (overflow === 'hidden' || overflowY === 'hidden') {
      patches.push({
        el: current,
        overflow: current.style.overflow,
        overflowY: current.style.overflowY,
      })
      current.style.overflow = 'visible'
      current.style.overflowY = 'visible'
    }
    current = current.parentElement
  }
  return patches
}

/**
 * Restaura os valores originais de overflow nos elementos salvos por
 * `patchHiddenOverflowAncestors`.
 *
 * @param {Array} patches - Array de patches para restaurar overflow
 */
export const restoreOverflowPatches = (patches) => {
  patches.forEach((patch) => {
    patch.el.style.overflow = patch.overflow
    patch.el.style.overflowY = patch.overflowY
  })
}

/**
 * Retorna os limites top e bottom do container.
 *
 * @param {Object} options - Opcoes
 * @param {HTMLElement} options.container - Container scrollavel
 * @param {number} options.viewportHeight - Altura da viewport
 * @returns {{ top: number, bottom: number }} Limites top e bottom
 */
const getDriveDragScrollBounds = ({ container, viewportHeight }) => {
  if (container && container.getBoundingClientRect) {
    const rect = container.getBoundingClientRect()
    return {
      top: rect.top,
      bottom: rect.bottom,
    }
  }

  return {
    top: 0,
    bottom: viewportHeight,
  }
}

/**
 * Retorna a direcao de scroll baseada na posicao do cliente.
 *
 * @param {Object} options - Opcoes
 * @param {number} options.clientY - Posicao vertical do cliente
 * @param {HTMLElement} options.container - Container scrollavel
 * @param {number} options.zone - Zona de atuacao
 * @param {number} options.viewportHeight - Altura da viewport
 * @returns {number} Direcao de scroll (-1, 0, 1)
 */
export const getDriveDragScrollDirection = ({
  clientY,
  container = null,
  zone = 80,
  viewportHeight = 0,
} = {}) => {
  if (typeof clientY !== 'number') {
    return 0
  }

  const bounds = getDriveDragScrollBounds({
    container,
    viewportHeight,
  })

  if (clientY < bounds.top + zone) {
    return -1
  }

  if (clientY > bounds.bottom - zone) {
    return 1
  }

  return 0
}

export const canDropOnFolder = ({ folder, showSnack = () => {} }) => {
  const BLACKLIST_FOLDERS_IDS = []

  if (!folder || folder.type !== 'folder') {
    return false
  }

  if (folder.locked && BLACKLIST_FOLDERS_IDS.includes(folder.id)) {
    showSnack({
      text: 'Não é possível mover itens para essa pasta.',
      color: 'error',
      button: true,
    })
    return false
  }

  return true
}
